/**
 * TimedGridConfig — the timed-grid layout parameters (visible hours window,
 * timezone, calendar style, day-vs-month view) read from a row's OWN attribute
 * bag.
 *
 * This is the de-coupling seam: the timed-grid layout (`timeline[column]`) and
 * the polymorphic body (`calendar-body`) source their config from the bound
 * LAYOUT row itself, instead of reaching UP to a `CalendarAliasRow` ancestor.
 * `CalendarAliasRow` delegates here too, so the alias and the body compute
 * identical config from one place — a layout is pure and never knows the
 * calendar alias exists.
 *
 * Pure + row-agnostic (a plain attribute bag in, a config out) so it is equally
 * usable from the DOM-free SDK rows-side and from the sheets-ui plugins.
 */
import type { Option } from "../../core/option.ts";
import { Json, type JsonObject } from "../../core/json.ts";
import { TimeOfDay, Tzname } from "@tento-chrono";

/**
 * How a calendar lays out its day: the visible hours and whether it shows a
 * timed grid.
 */
export interface TimedGridConfig {
  /** Visible day window (startHr–endHr). */
  hoursRange(): TimeOfDay.Range;
  /** `default` (timed grid) | `list` (no timed grid) | `custom`. */
  readonly calendarType: "default" | "list" | "custom";
  /** Axis timezone override; `null` = sheet/local. */
  readonly timezone: Option<Tzname>;
  readonly disableMonthlyView: boolean;
  /** Whether to paint the month grid or the timed grid at a given sheet scale. */
  effectiveView(sheetTimescale: string): "daily" | "monthly";
}

/** Values that go with `TimedGridConfig`: `BODY_PRESET`, `withChildOverride`, `fromAttributes`. */
export namespace TimedGridConfig {
  /**
   * The childSpec preset under which a calendar alias holds its body layout — and
   * thus the key of the per-child OVERRIDE slot (`alias.attributes.children
   * ["calendar-body"]`) where the body's timed-grid config (hours / type /
   * timezone / monthly) persists. Decision 1: one nested `children[preset]`
   * map, generalizing the existing `children[<preset>].visible` precedent.
   */
  export const BODY_PRESET = "calendar-body";

  const CALENDAR_TYPES: readonly TimedGridConfig["calendarType"][] = [
    "default",
    "list",
    "custom",
  ];

  /**
   * Resolve one cascade layer: merge a row's per-child OVERRIDE slot
   * (`bag.children[preset]`) OVER the bag's top-level values. The override wins,
   * the top-level is the read-compat fallback (legacy calendars persisted config
   * at the top level). Returns a flat bag ready for {@link fromAttributes}
   * (whose `?? 0 / ?? 24 / …` supplies the row-type default below both). Cheap
   * pure fold — every value is plain JSON.
   */
  export function withChildOverride(
    bag: JsonObject,
    preset: string,
  ): JsonObject {
    const slot = Json.child(Json.child(bag, "children"), preset);
    return slot ? { ...bag, ...slot } : { ...bag };
  }

  /**
   * Build a {@link TimedGridConfig} from a row's attribute bag. Mirrors the logic
   * previously inlined in the `CalendarAliasRow` getters (hoursRange / calendarType
   * / timezone / disableMonthlyView / effectiveView) verbatim, so moving the read
   * site from the alias to the layout row is behavior-preserving.
   */
  export function fromAttributes(
    attrs: JsonObject,
  ): TimedGridConfig {
    const startHr = Json.number(attrs, "startHr") ?? 0;
    const endHr = Json.number(attrs, "endHr") ?? 24;
    const disableMonthlyView = Json.boolean(attrs, "disableMonthlyView") ?? false;
    const timezone = Json.string(attrs, "timezone");
    return {
      hoursRange(): TimeOfDay.Range {
        if (!startHr && !endHr) return TimeOfDay.Range.DAY;
        return new TimeOfDay.Range(
          startHr === 0 ? TimeOfDay.ZERO : TimeOfDay.wrap({ hrs: startHr }),
          endHr === 0 ? TimeOfDay.ZERO : TimeOfDay.wrap({ hrs: endHr }),
          endHr >= 24,
        );
      },
      calendarType:
        Json.oneOf(attrs, "type", CALENDAR_TYPES) ?? "default",
      // A name the runtime does not know as an IANA zone reads as no zone.
      timezone: (timezone != null ? Tzname.parseOpt(timezone) : null) ?? null,
      disableMonthlyView,
      effectiveView(sheetTimescale: string): "daily" | "monthly" {
        return sheetTimescale === "month" && !disableMonthlyView
          ? "monthly"
          : "daily";
      },
    };
  }
}
